import { dailyKey } from "./engine.js";
import { GameSession, HINTS_PER_ROUND } from "./game-session.js";
import { TutorialSession } from "./tutorial.js";

export const STARTING_CAT_HELPS = 5;
export const MAX_CROSS_HINTS = 5;

/** Coordinates injected domain, catalog, and storage interfaces; knows no DOM. */
export class GameController {
  constructor({
    catalog,
    store,
    monetizedHints = false,
    now = Date.now,
    date = () => dailyKey(),
    schedule = (task) => globalThis.setTimeout(task, 160),
    cancel = (handle) => globalThis.clearTimeout(handle),
  }) {
    this.catalog = catalog;
    this.store = store;
    this.monetizedHints = monetizedHints;
    this.now = now;
    this.date = date;
    this.schedule = (task) => schedule(task);
    this.cancel = (handle) => cancel(handle);
    this.saveTask = null;
    this.listeners = new Set();
    this.revision = 0;
    const saved = store.load();
    const valid = Boolean(saved && [1, 2, 3, 4, 5].includes(saved.version));
    this.mode = valid && saved.mode === "daily" ? "daily" : "practice";
    this.tutorial = null;
    this.tutorialSeen = valid && saved.tutorialSeen === true;
    this.tutorialCompleted = valid && saved.tutorialCompleted === true;
    this.catHelpBalance =
      valid &&
      Number.isSafeInteger(saved.catHelpBalance) &&
      saved.catHelpBalance >= 0
        ? saved.catHelpBalance
        : STARTING_CAT_HELPS;
    this.prefs = { autoMark: true, dragMark: true, soundEffects: true, regionLabels: false };
    for (const key of Object.keys(this.prefs)) {
      if (
        valid &&
        typeof saved.prefs?.[key] === "boolean"
      )
        this.prefs[key] = saved.prefs[key];
    }
    this.stats = { wins: 0, bestTimes: {}, dailyDates: [] };
    if (valid && Number.isInteger(saved.stats?.wins) && saved.stats.wins >= 0)
      this.stats.wins = saved.stats.wins;
    for (const difficulty of ["easy", "medium", "hard"]) {
      const time = saved?.stats?.bestTimes?.[difficulty];
      if (valid && Number.isInteger(time) && time >= 0)
        this.stats.bestTimes[difficulty] = time;
    }
    if (valid && Array.isArray(saved.stats?.dailyDates))
      this.stats.dailyDates = saved.stats.dailyDates
        .filter((value) => /^\d{4}-\d{2}-\d{2}$/.test(value))
        .slice(-365);
    this.journeyLevel =
      valid &&
      saved.version >= 4 &&
      Number.isSafeInteger(saved.journeyLevel) &&
      saved.journeyLevel > 0
        ? saved.journeyLevel
        : Math.max(1, this.stats.wins - this.stats.dailyDates.length + 1);
    this.sessions = {
      practice: this.restore(valid && saved.practice),
      daily:
        valid && saved.daily?.date === date()
          ? this.restore(saved.daily)
          : null,
    };
    this.practiceLevel =
      valid &&
      saved.version >= 4 &&
      Number.isSafeInteger(saved.practiceLevel) &&
      saved.practiceLevel > 0 &&
      saved.practiceLevel <= this.journeyLevel
        ? saved.practiceLevel
        : Math.max(
            1,
            this.journeyLevel - (this.sessions.practice?.data.status === "won" ? 1 : 0),
          );
    // Untouched legacy opening screens can start the new guided journey.
    // An in-progress round retains its original board, cats, notes, and time.
    if (
      !this.sessions.practice ||
      (!(saved?.version >= 4) && !this.sessions.practice.data.started)
    )
      this.sessions.practice = this.createPractice();
    if (this.mode === "daily" && !this.sessions.daily)
      this.sessions.daily = this.createDaily();
    const savedCrossHints = valid && saved.hintWallet?.crossHints;
    this.hintWallet = Number.isSafeInteger(savedCrossHints) && savedCrossHints >= 0
      ? { crossHints: Math.min(MAX_CROSS_HINTS, savedCrossHints) }
      : monetizedHints
        ? { crossHints: Math.max(0, MAX_CROSS_HINTS - this.active.data.hints) }
        : null;
    this.active.resume();
    this.save();
  }
  get active() {
    return this.tutorial?.session ?? this.sessions[this.mode];
  }
  get needsTutorial() {
    return (
      !this.tutorialSeen && this.journeyLevel === 1 &&
      this.stats.wins === 0 && !this.sessions.practice.data.started &&
      !this.sessions.daily?.data.started
    );
  }
  get canSave() {
    return this.store.available !== false;
  }
  restore(saved) {
    if (
      !saved ||
      !Number.isInteger(saved.variant) ||
      saved.variant < 0 ||
      saved.variant > 7
    )
      return null;
    try {
      return new GameSession(this.catalog.get(saved.puzzleId, saved.variant), {
        saved,
        now: this.now,
      });
    } catch {
      return null;
    }
  }
  createPractice() {
    this.practiceLevel = this.journeyLevel;
    return new GameSession(
      this.catalog.practice({ level: this.journeyLevel }),
      { ordinal: this.journeyLevel, now: this.now },
    );
  }
  createDaily() {
    return new GameSession(this.catalog.daily(this.date()), {
      ordinal: 1,
      date: this.date(),
      now: this.now,
    });
  }
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  snapshot(effect = {}) {
    return {
      ...this.active.snapshot(),
      mode: this.tutorial ? "tutorial" : this.mode,
      tutorial: this.tutorial?.snapshot() ?? null,
      journeyLevel: this.journeyLevel,
      completedLevels: this.journeyLevel - 1,
      journeyReady: this.sessions.practice.data.status === "won",
      catHelpBalance: this.catHelpBalance,
      ...(this.monetizedHints ? { monetizedHints: true, crossHintBalance: this.hintWallet.crossHints } : {}),
      prefs: {
        ...this.prefs,
        ...(this.tutorial ? {
          autoMark: this.tutorial.step.autoMark === true,
          dragMark: false,
        } : {}),
      },
      stats: structuredClone(this.stats),
      revision: this.revision,
      effect,
    };
  }
  emit(effect = {}) {
    this.revision += 1;
    const snapshot = this.snapshot(effect);
    for (const listener of this.listeners) listener(snapshot);
  }
  save() {
    if (this.saveTask !== null) {
      this.cancel(this.saveTask);
      this.saveTask = null;
    }
    this.store.save({
      version: 5,
      mode: this.mode,
      tutorialSeen: this.tutorialSeen,
      tutorialCompleted: this.tutorialCompleted,
      journeyLevel: this.journeyLevel,
      practiceLevel: this.practiceLevel,
      catHelpBalance: this.catHelpBalance,
      ...(this.hintWallet ? { hintWallet: { crossHints: this.hintWallet.crossHints } } : {}),
      prefs: this.prefs,
      stats: this.stats,
      practice: this.sessions.practice?.serialize(),
      daily: this.sessions.daily?.serialize(),
    });
  }
  saveSoon() {
    if (this.saveTask !== null) this.cancel(this.saveTask);
    this.saveTask = this.schedule(() => {
      this.saveTask = null;
      this.save();
    });
  }
  perform(command, index) {
    if (this.tutorial) {
      const effect = this.tutorial.perform(command, index);
      if (effect.changed) this.emit(effect);
      return effect;
    }
    if (command === "paintMarks" && !this.prefs.dragMark) return {};
    if (command === "revealCat" && this.catHelpBalance === 0) return {};
    if (command === "hint" && this.monetizedHints && this.hintWallet.crossHints === 0) return {};
    const effect =
      command === "hint"
        ? this.active.hint({ autoMark: this.prefs.autoMark, limit: this.monetizedHints ? Infinity : HINTS_PER_ROUND })
        : this.active[command](index);
    if (!effect.changed) return effect;
    if (command === "revealCat") this.catHelpBalance -= 1;
    if (command === "hint" && this.monetizedHints) this.hintWallet.crossHints -= 1;
    if (effect.won) {
      if (this.monetizedHints) this.hintWallet.crossHints = Math.min(MAX_CROSS_HINTS, this.hintWallet.crossHints + 1);
      this.stats.wins += 1;
      if (this.mode === "practice" && this.practiceLevel === this.journeyLevel)
        this.journeyLevel += 1;
      const seconds = Math.max(1, Math.floor(this.active.elapsed() / 1000));
      const difficulty = this.active.puzzle.difficulty;
      if (
        this.stats.bestTimes[difficulty] === undefined ||
        seconds < this.stats.bestTimes[difficulty]
      )
        this.stats.bestTimes[difficulty] = seconds;
      if (this.mode === "daily" && !this.stats.dailyDates.includes(this.date()))
        this.stats.dailyDates.push(this.date());
    }
    this.emit(effect);
    this.saveSoon();
    return effect;
  }
  creditRewardedCatHint() {
    if (!this.monetizedHints || this.catHelpBalance !== 0) return false;
    this.catHelpBalance = 1;
    this.save();
    this.emit({ message: "Video complete. Cat hint earned." });
    return true;
  }
  newPuzzle() {
    if (this.tutorial) return;
    this.active.pause();
    const practice = this.sessions.practice;
    if (practice.data.status === "won")
      this.sessions.practice = this.createPractice();
    else if (practice.data.status === "lost")
      this.sessions.practice = new GameSession(practice.puzzle, {
        ordinal: practice.data.ordinal,
        now: this.now,
      });
    this.mode = "practice";
    this.active.resume();
    this.save();
    this.emit({ newPuzzle: true });
  }
  selectMode(mode) {
    if (this.tutorial) return;
    if (!["practice", "daily"].includes(mode)) return;
    this.active.pause();
    if (mode === "daily" && this.sessions.daily?.data.date !== this.date())
      this.sessions.daily = this.createDaily();
    this.mode = mode;
    this.active.resume();
    this.save();
    this.emit({ newPuzzle: true });
  }
  restart() {
    if (this.tutorial) return;
    const old = this.active;
    old.pause();
    this.sessions[this.mode] = new GameSession(old.puzzle, {
      ordinal: old.data.ordinal,
      date: old.data.date,
      now: this.now,
    });
    this.save();
    this.emit({ newPuzzle: true });
  }
  setPreference(key, value) {
    if (this.tutorial) return;
    if (!(key in this.prefs)) return;
    this.prefs[key] = Boolean(value);
    this.save();
    this.emit();
  }
  startTutorial() {
    if (this.tutorial) return;
    this.active.pause();
    this.tutorial = new TutorialSession({ now: this.now });
    this.tutorialSeen = true;
    this.save();
    this.emit({ newPuzzle: true });
  }
  nextTutorialStep() {
    if (!this.tutorial) return;
    if (this.tutorial.step.action === "finish") {
      this.finishTutorial(true);
    } else if (this.tutorial.next()) this.emit({ tutorialStep: true });
  }
  finishTutorial(completed = false) {
    if (!this.tutorial) return;
    this.tutorial.session.pause();
    this.tutorial = null;
    this.tutorialCompleted ||= completed;
    this.active.resume();
    this.save();
    this.emit({ newPuzzle: true });
  }
  pause() {
    this.active.pause();
    this.save();
  }
  resume() {
    this.active.resume();
  }
}
